import { DespatchAdviceCategory } from '@common/enums';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { IDespatchAdviceDefaultSettings } from '~/@store/beastIntegration';
import { ICompanySearchRecord } from '~/@store/companies';
import { IDespatchAdviceSettings } from '~/@store/dumpLoads';
import { PeppolParticipantStatus } from '~/graphql';
import i18n from '~/i18n';
import { getPeppolConnectionStatus } from '~/utils/dumpLoad';

export type IDespatchAdviceSettingsFormValues = {
    isActive: boolean;
    category: DespatchAdviceCategory;
    consignee: ICompanySearchRecord | null;
    despatchParty: ICompanySearchRecord | null;
    buyer: ICompanySearchRecord | null;
    seller: ICompanySearchRecord | null;
    allowSelect: boolean;
    pricePerTon: number | null;
    isPriceMandatory: boolean;
};

export const useDumpLoadPeppolSettingsFormik = (
    settings: IDespatchAdviceSettings | null,
    category: DespatchAdviceCategory,
    defaultSettings: IDespatchAdviceDefaultSettings,
    isPriceMandatory: boolean = false
) =>
    useFormik<IDespatchAdviceSettingsFormValues>({
        initialValues: {
            category,
            isActive: !!settings,
            consignee: settings?.consignee || defaultSettings.consignee || null,
            despatchParty: settings?.despatchParty || defaultSettings.despatchParty || null,
            buyer: settings?.buyer || null,
            seller: settings?.seller || null,
            pricePerTon: settings?.pricePerTon || null,
            allowSelect: defaultSettings.allowSelect,
            isPriceMandatory,
        },
        validationSchema: Yup.object().shape({
            consignee: Yup.object<ICompanySearchRecord>()
                .nullable()
                .test(
                    'Should be specified',
                    i18n.DespatchAdviceSettingsDialog.shouldBeSpecified,
                    function (this, value) {
                        return !!value || !this.parent.isActive;
                    }
                )
                .test('Is connected to Peppol', '', function (this, value) {
                    if (!value || !this.parent.isActive) return true;

                    return getPeppolConnectionStatus(value.id).then(status => {
                        if (status === PeppolParticipantStatus.NOT_CONNECTED)
                            return this.createError({
                                message: i18n.DespatchAdviceSettingsDialog.notConnected,
                                path: 'consignee',
                            });

                        if (status === PeppolParticipantStatus.DOC_MISSING)
                            return this.createError({
                                message: i18n.DespatchAdviceSettingsDialog.docMissing,
                                path: 'consignee',
                            });

                        return true;
                    });
                }),
            despatchParty: Yup.object<ICompanySearchRecord>()
                .nullable()
                .test('Is connected to Peppol', '', function (this, value) {
                    if (!value || !this.parent.isActive) return true;

                    return getPeppolConnectionStatus(value.id).then(status => {
                        if (status === PeppolParticipantStatus.NOT_CONNECTED)
                            return this.createError({
                                message: i18n.DespatchAdviceSettingsDialog.notConnected,
                                path: 'despatchParty',
                            });

                        if (status === PeppolParticipantStatus.DOC_MISSING)
                            return this.createError({
                                message: i18n.DespatchAdviceSettingsDialog.docMissing,
                                path: 'despatchParty',
                            });

                        return true;
                    });
                }),
            buyer: Yup.object()
                .nullable()
                .test(
                    'Should be specified',
                    i18n.DespatchAdviceSettingsDialog.shouldBeSpecified,
                    function (this, value) {
                        return !!value || !this.parent.isActive;
                    }
                ),
            seller: Yup.object()
                .nullable()
                .test(
                    'Should be specified',
                    i18n.DespatchAdviceSettingsDialog.shouldBeSpecified,
                    function (this, value) {
                        return !!value || !this.parent.isActive;
                    }
                ),
            pricePerTon: Yup.number()
                .nullable()
                .test(
                    'Should be specified',
                    i18n.DespatchAdviceSettingsDialog.shouldBeSpecified,
                    function (this, value) {
                        return this.parent.isActive && this.parent.isPriceMandatory ? !!value : true;
                    }
                ),
        }),
        onSubmit: () => {},
    });
